import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { useLenis } from "@/hooks/useLenis";
// Vorübergehend: Messhilfe für In-App-Browser, nur mit ?debug=viewport
import { ViewportDebug } from "@/components/ViewportDebug";
import Landing from "@/pages/Landing";
import ForClubs from "@/pages/ForClubs";
import ForCompanies from "@/pages/ForCompanies";
import Pricing from "@/pages/Pricing";
import Demo from "@/pages/Demo";
import Waitlist from "@/pages/Waitlist";
import WaitlistConfirm from "@/pages/WaitlistConfirm";
import Login from "@/pages/Login";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Faq from "@/pages/Faq";
import Imprint from "@/pages/legal/Imprint";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Cookies from "@/pages/legal/Cookies";
import NotFound from "@/pages/NotFound";

// Beim Neuladen soll der Browser die Scrollposition nicht selbst
// wiederherstellen. Die Google-App auf dem iPhone rechnet dabei ihre
// Adressleiste nicht ein, die Seite rutscht dahinter. Nach oben springen
// übernimmt ScrollToTop.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

/** Setzt bei jedem Seitenwechsel zurück nach oben - auch wenn Lenis läuft. */
function ScrollToTop({ lenisRef }) {
  const { pathname } = useLocation();
  const lastPath = useRef(pathname);

  useEffect(() => {
    // Nur bei echten Seitenwechseln, nicht beim ersten Laden: Dort steht die
    // Seite schon oben, ein zusätzlicher Sprung kann sie in der Google-App
    // hinter deren Adressleiste schieben. Vergleich über den letzten Pfad,
    // weil StrictMode den Effekt im Entwicklungsmodus doppelt ausführt.
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    // Erst nativ nach oben, dann Lenis an die neue Position angleichen.
    // lenis.scrollTo(0) allein bricht ab, wenn Lenis' internes Ziel schon 0
    // ist, auch wenn die Seite tatsächlich woanders steht.
    window.scrollTo(0, 0);
    lenisRef.current?.reset();
  }, [pathname, lenisRef]);

  return null;
}

function App() {
  const lenisRef = useLenis();

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop lenisRef={lenisRef} />
        <ViewportDebug />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/fuer-vereine" element={<ForClubs />} />
          <Route path="/fuer-unternehmen" element={<ForCompanies />} />
          <Route path="/preisstruktur" element={<Pricing />} />
          <Route path="/demo" element={<Demo />} />
          <Route path="/warteliste" element={<Waitlist />} />
          {/* Ziel des Bestätigungslinks aus der Mail (Double-Opt-In) */}
          <Route path="/warteliste/bestaetigen" element={<WaitlistConfirm />} />
          <Route path="/login" element={<Login />} />
          {/* Alte Adresse, damit bestehende Links weiter funktionieren */}
          <Route path="/registrieren" element={<Navigate to="/warteliste" replace />} />
          <Route path="/ueber-uns" element={<About />} />
          <Route path="/kontakt" element={<Contact />} />
          <Route path="/haeufige-fragen" element={<Faq />} />
          <Route path="/impressum" element={<Imprint />} />
          <Route path="/datenschutz" element={<Privacy />} />
          <Route path="/agb" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
